import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AppShell } from '@/shared/ui/app-shell'
import { BottomNav } from '@/widgets/bottom-nav'
import { PageHeader, PageHeaderSpacer } from '@/shared/ui/page-header'
import { useTelegram, useBackButton } from '@/shared/lib/telegram'
import { requireAuth } from '@/shared/lib/router'
import { useFavorites } from '@/features/favorites/api/use-favorites'
import { FavoriteButton } from '@/features/favorites/ui'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { OptimizedImage } from '@/shared/ui'
import { Heart, ShoppingBag, ArrowRight } from 'lucide-react'
import { formatPrice } from '@/shared/lib/format'

export const Route = createFileRoute('/want-to-drink')({
  component: WantToDrinkPage,
})

/**
 * Страница: Хочу попробовать (Избранное)
 * 
 * Страница для управления списком желаний (товары, которые пользователь хочет попробовать).
 * Использует избранное как список желаний.
 * Требует авторизации.
 * 
 * @example
 * Маршрут: /want-to-drink
 */
function WantToDrinkPage() {
  const { initData, hapticFeedback } = useTelegram()
  const navigate = useNavigate()
  
  // Нативная кнопка "Назад" в заголовке Telegram
  useBackButton(true)
  
  // Проверяем авторизацию
  requireAuth(initData)

  const { data, isLoading, error, refetch } = useFavorites()
  const favorites = data?.data ?? []

  // Подсчёт товаров с правильным склонением
  const getProductCountText = (count: number) => {
    if (count === 0) return 'Нет товаров'
    if (count === 1) return '1 товар'
    if (count >= 2 && count <= 4) return `${count} товара`
    return `${count} товаров`
  }

  return (
    <AppShell showBottomNav>
      {/* Фиксированная шапка */}
      <PageHeader 
        title="Хочу попробовать" 
        subtitle={isLoading ? undefined : getProductCountText(favorites.length)}
        hideBackButton
      />

      {/* Контент страницы */}
      <div className="flex-1 bg-paper dark:bg-dark-bg">
        <div className="container mx-auto px-4 py-4">
          {/* Spacer для компенсации высоты шапки */}
          <PageHeaderSpacer />

          {/* Состояние загрузки */}
          {isLoading && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="aspect-square w-full rounded-2xl" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          )}

          {/* Состояние ошибки */}
          {error && (
            <div className="text-center py-12 animate-in fade-in duration-300">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                <Heart className="h-8 w-8 text-red-500 dark:text-red-400" />
              </div>
              <p className="text-error mb-4">Не удалось загрузить список желаний</p>
              <Button
                variant="secondary"
                onClick={() => refetch()}
              >
                Попробовать снова
              </Button>
            </div>
          )}

          {/* Пустой список */}
          {!isLoading && !error && favorites.length === 0 && (
            <div className="text-center py-16 animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 dark:bg-dark-elevated rounded-full mb-6">
                <Heart className="h-10 w-10 text-gray-300 dark:text-gray-600" />
              </div>
              <h2 className="text-xl font-bold mb-2 text-ink dark:text-white">
                Список желаний пуст
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-xs mx-auto">
                Добавляйте понравившиеся товары в избранное, чтобы они появились здесь
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  hapticFeedback?.impactOccurred('light')
                  navigate({ to: '/catalog/$section', params: { section: 'tea' } })
                }}
                className="rounded-full px-6"
              >
                Перейти в каталог
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Список товаров */}
          {!isLoading && !error && favorites.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-3 pt-2 animate-in fade-in duration-200">
                {favorites.map((item, index) => (
                  <FavoriteProductCard
                    key={item.id}
                    item={item}
                    index={index}
                    onProductClick={() => {
                      hapticFeedback?.impactOccurred('light')
                      navigate({ to: '/product/$productId', params: { productId: item.productId } })
                    }}
                  />
                ))}
              </div>

              {/* Кнопка перехода в каталог */}
              <div className="pt-6 pb-4">
                <Button
                  variant="secondary"
                  className="w-full rounded-2xl h-14"
                  onClick={() => {
                    hapticFeedback?.impactOccurred('light')
                    navigate({ to: '/catalog/$section', params: { section: 'tea' } })
                  }}
                >
                  <ShoppingBag className="h-5 w-5 mr-2" />
                  Продолжить покупки
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
      <BottomNav />
    </AppShell>
  )
}

/**
 * Карточка товара для списка избранного
 */
import type { FavoriteItemResponse } from '@/features/favorites/api/use-favorites'

interface FavoriteProductCardProps {
  item: FavoriteItemResponse
  index: number
  onProductClick: () => void
}

function FavoriteProductCard({ item, index, onProductClick }: FavoriteProductCardProps) {
  const product = item.product
  const name = product?.name || 'Товар'
  const image = product?.imageUrl || '/placeholder-product.svg'
  const price = product?.isOnSale && product?.salePriceAmount 
    ? product.salePriceAmount 
    : (product?.priceAmount || 0)
  const originalPrice = product?.isOnSale ? product?.priceAmount : undefined
  const isTea = item.productType === 'tea'
  const isInStock = product?.isInStock ?? true
  
  return (
    <div
      className="relative group animate-in fade-in slide-in-from-bottom-2 duration-200"
      style={{ animationDelay: `${Math.min(index * 50, 200)}ms` }}
    >
      {/* Карточка товара */}
      <div
        onClick={onProductClick}
        className="bg-white dark:bg-dark-card rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.25)] border border-gray-100 dark:border-dark-border/20 cursor-pointer active:scale-[0.98] transition-transform duration-150"
      >
        {/* Изображение */}
        <div className="relative aspect-square bg-gray-50 dark:bg-dark-elevated overflow-hidden">
          <OptimizedImage
            src={image}
            alt={name}
            preset="card"
            wrapperClassName="h-full w-full"
            className="object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          
          {/* Градиент для читаемости */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
          
          {/* Overlay для "Нет в наличии" */}
          {!isInStock && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white font-semibold text-sm">Нет в наличии</span>
            </div>
          )}
          
          {/* Кнопка избранного */}
          <div className="absolute top-2 right-2">
            <FavoriteButton
              productId={item.productId}
              variant="card"
              size="md"
              animated
            />
          </div>
        </div>
        
        {/* Контент */}
        <div className="p-3 space-y-1.5">
          <h3 className="text-sm font-semibold text-ink dark:text-white line-clamp-2 leading-tight min-h-[2.5rem]">
            {name}
          </h3>
          
          <div className="flex items-baseline gap-1.5 pt-0.5">
            {product?.isOnSale && originalPrice ? (
              <>
                <span className="text-base font-bold text-primary dark:text-primary-light">
                  {formatPrice(isTea ? price * 10 : price)}
                </span>
                <span className="text-xs text-gray-400 line-through">
                  {formatPrice(isTea ? (originalPrice || 0) * 10 : (originalPrice || 0))}
                </span>
              </>
            ) : (
              <span className="text-base font-bold text-primary dark:text-primary-light">
                {price > 0 ? formatPrice(isTea ? price * 10 : price) : '—'}
              </span>
            )}
            {isTea && price > 0 && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                за 10г
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
